/**
 * Die Einrichtung von i18next.
 *
 * **Von Anfang an, auch wenn vorerst nur Deutsch gepflegt wird** (`CLAUDE.md` Regel 6). Der
 * Grund ist nicht die zweite Sprache, sondern die erste: sichtbare Texte an einer Stelle statt
 * verstreut im Code sind der Unterschied zwischen „Formulierung ändern" und „Formulierung
 * suchen".
 *
 * **Ein fehlender Schlüssel sieht kaputt aus.** i18next gibt im Standardfall den Schlüssel
 * zurück, und `lobby.waiting` liest sich auf einem Bildschirm wie eine Beschriftung — ein
 * Testspieler würde es nicht melden. Mit den Winkeln drumherum ist es sofort ein Fehler und
 * keine Beschriftung.
 *
 * **Die Namensräume sind flache Bereiche**, keine Hierarchie je Komponente: wer einen Text
 * ändern will, sucht ihn nach Thema (`errors`, `pieces`, `sectors`), nicht nach Dateipfad. Eine
 * Komponente, die umzieht, soll keine Übersetzungsdatei mitnehmen.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { de } from './de.js';

export { de } from './de.js';
export type { Translations } from './de.js';

/**
 * Die beiden Fehlerursachen, die nur der Client kennt (`api/transport.ts`).
 *
 * Als Laufzeitliste und nicht nur als Typ, damit der Test über **alle** Codes laufen kann. Die
 * Codes des Servers liefert `@flaghex/contracts`; diese beiden hat dort niemand, weil sie dort
 * nicht entstehen.
 */
export const CLIENT_FAILURE_CODES = ['NETWORK', 'MALFORMED'] as const;

/** Die Namensräume — identisch mit den Bereichen in `de.ts`. */
export const NAMESPACES = Object.keys(de) as readonly (keyof typeof de)[];

// Die Typangabe ist nötig und nicht nur sauber: ohne sie zeigt der abgeleitete Typ auf einen
// Pfad in `node_modules`, und `tsc` lehnt das als nicht portabel ab (TS2742).
export const i18n: I18nInstance = i18next.createInstance();

await i18n.use(initReactI18next).init({
  lng: 'de',
  fallbackLng: 'de',
  defaultNS: 'common',
  ns: NAMESPACES,
  resources: { de },
  // Deutsch braucht keine Regionalvarianten; `de-DE` und `de` sollen nicht zwei Bündel sein.
  load: 'languageOnly',
  interpolation: {
    // React maskiert selbst. Doppelt maskiert wird aus „Stoßer" ein „Sto&#223;er".
    escapeValue: false,
  },
  // Siehe Modulkopf: sichtbar kaputt statt unauffällig falsch.
  parseMissingKeyHandler: (key) => `⟨${key}⟩`,
});

/**
 * Übersetzt außerhalb von React — für Fehlermeldungen, die kein Bauteil erzeugt.
 *
 * Innerhalb von Komponenten gilt `useTranslation`, damit ein Sprachwechsel neu zeichnet.
 */
export const t = i18n.t.bind(i18n);
